import { useSyncExternalStore } from 'react';
import { ca } from '../i18n/ca';
import {
  editTestStep,
  fetchMyWebCards,
  finishTest,
  pageState,
  type StepAnswer,
  type TestTask,
} from '../api/tests';
import { errorText } from './useSessionLive';

/**
 * The web test cards the reader took and has not finished, for the «Prova en
 * curs» panel (the owner, 2026-10-12). Kept outside React on purpose: every
 * page mounts its own `Shell`, so a panel that kept its typed note, its place
 * among the steps or a save still on its way in component state would lose
 * them at each «Obre».
 *
 * Saving follows memory `a-save-that-answers-late-must-not-overwrite-the-page`:
 * one request at a time for a step, whatever is asked meanwhile merged and sent
 * after it (so the last word asked is the last one written, never the reverse);
 * an answer that arrives out of turn never replaces a card's newer state; and a
 * typed note lives in a draft until the platform echoes that very text.
 */

export type SaveState = 'saving' | 'saved' | 'error';

/**
 * Where the panel's one «Acaba la prova» button stands (the owner, 2026-10-13):
 * working; `failed` (nothing was finished: the card is still the reader's);
 * `done` (finished, which is also closed: one act since 2026-10-13). Kept here, not in the component,
 * because every page mounts its own panel and the sentence must outlive one.
 */
export interface FinishState {
  cardId: number;
  title: string;
  status: 'working' | 'failed' | 'done';
  text: string;
}

export interface WebCardsState {
  cards: TestTask[];
  /** Whether the list was ever asked for and answered. */
  loaded: boolean;
  /** The card the panel shows; null is the last taken. */
  selected: number | null;
  /** The step the panel is on, by card; absent is the first unanswered. */
  at: Record<number, number>;
  /** Typed notes the platform has not echoed yet, by `card:step`. */
  drafts: Record<string, string>;
  /** Answers chosen and not echoed yet, by `card:step`. */
  wanted: Record<string, StepAnswer>;
  saving: Record<string, SaveState>;
  finish: FinishState | null;
}

/** How long after the last key a typed note is sent. */
export const NOTE_DELAY_MS = 700;

const EMPTY: WebCardsState = {
  cards: [],
  loaded: false,
  selected: null,
  at: {},
  drafts: {},
  wanted: {},
  saving: {},
  finish: null,
};

let state: WebCardsState = EMPTY;
const listeners = new Set<() => void>();

function set(next: Partial<WebCardsState>) {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const snapshot = () => state;

export function useWebCards(): WebCardsState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export const webCardsState = snapshot;
export const stepKey = (cardId: number, stepId: string) => `${cardId}:${stepId}`;

/** Whether the reader is already on the page a step starts on: the path, and the query when the step names one. */
export function onStepPage(url: string, pathname: string, search: string): boolean {
  const bare = url.split('#')[0];
  return bare.includes('?') ? bare === pathname + search : bare === pathname;
}

/** The reader's own word on a step, as the platform holds it. */
export function myStep(
  card: TestTask,
  stepId: string,
): { answer: StepAnswer | null; note: string | null } {
  const mine = (card.answers ?? []).filter((a) => a.stepId === stepId && a.mine);
  const answer = [...mine].reverse().find((a) => a.answer)?.answer ?? null;
  const note = [...mine].reverse().find((a) => a.note)?.note ?? null;
  return { answer: (answer as StepAnswer | null) ?? null, note };
}

/** A card the panel holds: a web card, taken by the reader and not finished. */
const held = (card: TestTask | null | undefined): boolean =>
  !!card &&
  card.medium === 'web' &&
  card.state === 'claimed' &&
  card.mine !== false &&
  Array.isArray(card.steps);

/** Bumped by a reset: whatever was asked before it is another account's, and is dropped. */
let generation = 0;
/** Every request takes the next number; a card keeps the number of the answer it shows. */
let counter = 0;
const shown = new Map<number, number>();
let loading: Promise<void> | null = null;

/** Takes a card's newer state; false when what is shown came from a later request. */
function apply(card: TestTask, seq: number): boolean {
  if ((shown.get(card.id) ?? 0) > seq) return false;
  shown.set(card.id, seq);
  const known = state.cards.some((c) => c.id === card.id);
  if (!held(card)) {
    if (known) set({ cards: state.cards.filter((c) => c.id !== card.id) });
    return true;
  }
  set({
    cards: known ? state.cards.map((c) => (c.id === card.id ? card : c)) : [card, ...state.cards],
  });
  return true;
}

/**
 * Reads the list. Once, unless `force`: the page that just took or finished a
 * card asks again. Never throws: a list that cannot be read leaves the panel
 * as it was.
 */
export function loadWebCards(force = false): Promise<void> {
  if (loading) return force ? loading.then(() => loadWebCards(true)) : loading;
  if (state.loaded && !force) return Promise.resolve();
  const asked = generation;
  const seq = ++counter;
  loading = fetchMyWebCards()
    .then((list) => {
      if (asked !== generation) return;
      const fresh = (Array.isArray(list) ? list : []).filter((c): c is TestTask => held(c));
      // A card answered since this list was asked for keeps what the answer brought.
      const cards = fresh.map((card) => {
        const current = state.cards.find((c) => c.id === card.id);
        if (current && (shown.get(card.id) ?? 0) > seq) return current;
        shown.set(card.id, seq);
        return card;
      });
      set({ cards, loaded: true });
    })
    .catch(() => {
      if (asked === generation) set({ loaded: true });
    })
    .finally(() => {
      loading = null;
    });
  return loading;
}

/** A card as a page just got it back (taken, answered, finished): the panel follows without asking again. */
export function putWebCard(card: TestTask) {
  if (card.medium !== 'web') return;
  apply(card, ++counter);
}

interface Slot {
  cardId: number;
  stepId: string;
  inflight: boolean;
  pending: { answer?: StepAnswer; note?: string } | null;
  timer: number | null;
}
const slots = new Map<string, Slot>();

function saving(key: string, value: SaveState) {
  set({ saving: { ...state.saving, [key]: value } });
}

function without<T>(record: Record<string, T>, key: string): Record<string, T> {
  const next = { ...record };
  delete next[key];
  return next;
}

function flush(key: string) {
  const slot = slots.get(key);
  if (!slot || slot.inflight || !slot.pending) return;
  if (slot.timer !== null) {
    window.clearTimeout(slot.timer);
    slot.timer = null;
  }
  const body = slot.pending;
  slot.pending = null;
  slot.inflight = true;
  const asked = generation;
  const seq = ++counter;
  let failed = false;
  saving(key, 'saving');
  editTestStep(
    slot.cardId,
    slot.stepId,
    // Where an answer was given from goes with the answer, not with a note alone.
    body.answer ? { ...body, context: pageState() } : body,
  )
    .then((card) => {
      if (asked !== generation) return;
      if (apply(card, seq)) settle(key, slot, card);
      saving(key, slot.pending ? 'saving' : 'saved');
    })
    .catch(() => {
      if (asked !== generation) return;
      failed = true;
      // What was not written is still wanted, under whatever was asked since.
      slot.pending = { ...body, ...slot.pending };
      saving(key, 'error');
      // The card may be over (closed, cancelled): the list says so.
      void loadWebCards(true);
    })
    .finally(() => {
      slot.inflight = false;
      // A failed save is tried again with the reader's next word, not in a loop.
      if (!failed && asked === generation && slot.pending && slot.timer === null) flush(key);
    });
}

/** What the platform echoed is no longer a draft; what was typed or chosen since still is. */
function settle(key: string, slot: Slot, card: TestTask) {
  const echo = myStep(card, slot.stepId);
  const next: Partial<WebCardsState> = {};
  if (
    key in state.wanted &&
    slot.pending?.answer === undefined &&
    echo.answer === state.wanted[key]
  ) {
    next.wanted = without(state.wanted, key);
  }
  if (
    key in state.drafts &&
    slot.pending?.note === undefined &&
    (echo.note ?? '') === state.drafts[key].trim()
  ) {
    next.drafts = without(state.drafts, key);
  }
  if (next.wanted || next.drafts) set(next);
}

function queue(
  cardId: number,
  stepId: string,
  patch: { answer?: StepAnswer; note?: string },
  delay: number,
) {
  const key = stepKey(cardId, stepId);
  const slot = slots.get(key) ?? { cardId, stepId, inflight: false, pending: null, timer: null };
  slots.set(key, slot);
  slot.pending = { ...slot.pending, ...patch };
  if (slot.timer !== null) {
    window.clearTimeout(slot.timer);
    slot.timer = null;
  }
  if (delay > 0) {
    slot.timer = window.setTimeout(() => {
      slot.timer = null;
      flush(key);
    }, delay);
  } else {
    flush(key);
  }
}

/** The reader's answer to a step: shown at once, written in its turn. */
export function answerStep(cardId: number, stepId: string, answer: StepAnswer) {
  // A refusal to finish is about the answers as they were: a new one starts the reading again.
  set({
    wanted: { ...state.wanted, [stepKey(cardId, stepId)]: answer },
    ...(state.finish?.status === 'failed' ? { finish: null } : {}),
  });
  queue(cardId, stepId, { answer }, 0);
}

/** The reader's note on a step, as it is typed: kept as a draft, sent a moment after the last key. */
export function noteStep(cardId: number, stepId: string, text: string) {
  set({ drafts: { ...state.drafts, [stepKey(cardId, stepId)]: text } });
  queue(cardId, stepId, { note: text }, NOTE_DELAY_MS);
}

/** Sends every note still waiting for its moment: the field was left, or the step changed. */
export function flushNotes() {
  slots.forEach((slot, key) => {
    if (slot.timer !== null) {
      window.clearTimeout(slot.timer);
      slot.timer = null;
      flush(key);
    }
  });
}

export function selectWebCard(cardId: number) {
  set({ selected: cardId });
}

export function goToStep(cardId: number, index: number) {
  set({ at: { ...state.at, [cardId]: index } });
}

/** How long the «acabada» line stays before the panel goes away. */
export const FINISH_NOTICE_MS = 6000;
let finishTimer: number | null = null;

function clearFinishTimer() {
  if (finishTimer !== null) {
    window.clearTimeout(finishTimer);
    finishTimer = null;
  }
}

/** The sentence under the panel's button is read and put away. */
export function dismissFinish() {
  clearFinishTimer();
  if (state.finish && state.finish.status !== 'working') set({ finish: null });
}

/** Whether something of the card is still being written or is about to be. */
function writing(cardId: number): boolean {
  return [...slots.values()].some((s) => s.cardId === cardId && (s.inflight || s.timer !== null));
}

/**
 * Gives the card as finished, which closes it in the same act (the owner,
 * 2026-10-13: the platform's `done` is the close, for everybody). What the
 * reader typed last is written first: a note sent after the finish would be
 * refused. A failure says so and leaves the card as it was.
 */
export async function finishWebCard(card: TestTask): Promise<void> {
  if (state.finish?.status === 'working') return;
  const t = ca.testPanel;
  const asked = generation;
  const now = (status: FinishState['status'], text = '') =>
    set({ finish: { cardId: card.id, title: card.title, status, text } });
  clearFinishTimer();
  now('working');
  flushNotes();
  for (let i = 0; i < 100 && writing(card.id); i += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 50));
  }
  if (asked !== generation) return;
  let finished: TestTask;
  try {
    finished = await finishTest(card.id, {});
  } catch (e) {
    if (asked === generation) now('failed', t.finishFailed(errorText(e)));
    return;
  }
  if (asked !== generation) return;
  // Only now does the card leave the panels of every page: it stayed, working, while the call ran.
  putWebCard(finished);
  now('done', t.finished);
  finishTimer = window.setTimeout(() => {
    finishTimer = null;
    if (state.finish?.status === 'done') set({ finish: null });
  }, FINISH_NOTICE_MS);
}

/** Nobody's any more: the account signed out. Also what a test starts from. */
export function resetWebCards() {
  generation += 1;
  clearFinishTimer();
  slots.forEach((slot) => {
    if (slot.timer !== null) window.clearTimeout(slot.timer);
  });
  slots.clear();
  shown.clear();
  loading = null;
  if (state !== EMPTY) {
    state = EMPTY;
    listeners.forEach((listener) => listener());
  }
}
